// Real, persisted thread view with image attachments.
// Off-platform content filtering has been removed — users can freely share
// contact info and external links. Messages are still stored for manual
// admin review of policy violations.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Eye, MessageSquare, ImagePlus, X as XIcon, Package } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import {
  listThreadMessages,
  markThreadRead,
  type ThreadMessageAttachment,
} from "@/lib/messages-page.functions";
import { sendMessage } from "@/lib/messages.functions";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

export const Route = createFileRoute("/_authenticated/messages/$threadId")({
  head: () => ({ meta: [{ title: "Conversation — Want To Buy" }] }),
  component: ThreadView,
});

function timeStamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" });
}

const MAX_ATTACHMENTS = 6;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = /^image\/(jpeg|jpg|png|webp|gif)$/i;

function ThreadView() {
  const { threadId } = Route.useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const listFn = useServerFn(listThreadMessages);
  const sendFn = useServerFn(sendMessage);
  const markRead = useServerFn(markThreadRead);

  const { data, isLoading, error } = useQuery({
    queryKey: ["thread", threadId],
    queryFn: () => listFn({ data: { thread_id: threadId } }),
    refetchInterval: 5000,
  });

  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const previewUrls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previewUrls.forEach((u) => URL.revokeObjectURL(u)), [previewUrls]);

  useEffect(() => {
    markRead({ data: { thread_id: threadId } }).catch(() => {});
    qc.invalidateQueries({ queryKey: ["notifications", "header"] });
  }, [threadId, data?.messages.length, markRead, qc]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [data?.messages.length]);

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    const valid = picked.filter((f) => ACCEPT.test(f.type) && f.size <= MAX_BYTES);
    if (valid.length !== picked.length) toast.error("Only JPG/PNG/WebP/GIF under 10 MB are accepted.");
    setFiles((prev) => [...prev, ...valid].slice(0, MAX_ATTACHMENTS));
    e.target.value = "";
  }

  function removeAt(i: number) {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if ((!body && files.length === 0) || sending || !user?.id) return;
    setSending(true);
    try {
      const uploaded: { path: string; content_type: string }[] = [];
      if (files.length > 0) {
        const batch = crypto.randomUUID();
        for (let i = 0; i < files.length; i++) {
          const f = files[i];
          const ext = f.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
          const path = `${user.id}/${threadId}/${batch}-${Date.now()}-${i}.${ext}`;
          const { error: upErr } = await supabase.storage
            .from("message-attachments")
            .upload(path, f, { contentType: f.type, upsert: false });
          if (upErr) throw new Error(`Upload failed: ${upErr.message}`);
          uploaded.push({ path, content_type: f.type });
        }
      }
      const res = await sendFn({ data: { thread_id: threadId, body, attachments: uploaded } });
      if (!res.blocked) {
        setDraft("");
        setFiles([]);
      }
      qc.invalidateQueries({ queryKey: ["thread", threadId] });
      qc.invalidateQueries({ queryKey: ["messages", "threads"] });
    } catch (e2) {
      toast.error(e2 instanceof Error ? e2.message : "Failed to send message");
    } finally {
      setSending(false);
    }
  }

  return (
    <AppShell>
      <div className="mb-3 flex items-center gap-2 text-sm">
        <Link to="/messages" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> All messages
        </Link>
      </div>
      <div className="panel p-5">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <h1 className="font-display text-xl font-bold">Conversation</h1>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-md border border-border bg-background/40 px-3 py-2 text-[11px] text-muted-foreground">
          <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          Messages may be reviewed for policy compliance and dispute resolution.
        </div>

        <div
          ref={listRef}
          className="mt-4 max-h-[60vh] min-h-[220px] space-y-2 overflow-y-auto rounded-md border border-border bg-background/40 p-3"
        >
          {data?.orderLink && (
            <Link
              to={data.orderLink.href as never}
              className="mb-2 flex items-center gap-3 rounded-md border border-primary/30 bg-primary/5 p-2 hover:border-primary hover:bg-primary/10"
            >
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
                {data.orderLink.image_url ? (
                  <img
                    src={data.orderLink.image_url}
                    alt={data.orderLink.title}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="grid h-full w-full place-items-center">
                    <Package className="h-4 w-4 text-primary" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {data.orderLink.kind === "buy_order" ? "Linked buy order" : "Linked order"}
                </div>
                <div className="truncate text-sm font-semibold text-foreground">
                  {data.orderLink.title}
                </div>
              </div>
              <span className="shrink-0 text-xs text-primary">View →</span>
            </Link>
          )}
          {isLoading && <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>}
          {error && <div className="p-6 text-center text-sm text-destructive">Failed to load thread.</div>}
          {!isLoading && (data?.messages ?? []).length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No messages yet — start the conversation below.
            </div>
          )}
          {(data?.messages ?? []).map((m) => {
            const mine = m.sender_id === user?.id;
            const atts = m.attachments ?? [];
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    mine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                  }`}
                >
                  {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                  {atts.length > 0 && (
                    <div className={`grid gap-1.5 ${atts.length === 1 ? "grid-cols-1" : "grid-cols-2"} ${m.body ? "mt-2" : ""}`}>
                      {atts.map((a: ThreadMessageAttachment) => (
                        <button
                          type="button"
                          key={a.path}
                          onClick={() => a.url && setLightboxUrl(a.url)}
                          className="block aspect-square overflow-hidden rounded-md border border-border/50 bg-background/60"
                        >
                          {a.url ? (
                            <img src={a.url} alt="Attachment" className="h-full w-full object-cover" loading="lazy" />
                          ) : (
                            <div className="grid h-full w-full place-items-center text-[10px] text-muted-foreground">image</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className={`mt-1 text-[10px] ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {timeStamp(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {files.length > 0 && (
          <div className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-6">
            {previewUrls.map((url, i) => (
              <div key={i} className="relative aspect-square overflow-hidden rounded-md border border-border">
                <img src={url} alt={`Attachment ${i + 1}`} className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  aria-label="Remove attachment"
                  className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-black/70 text-white hover:bg-black"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={submit} className="mt-3 flex gap-2">
          <label
            className="grid h-10 w-10 shrink-0 cursor-pointer place-items-center rounded-md border border-border bg-background text-muted-foreground hover:border-primary hover:text-foreground"
            title="Attach images"
          >
            <ImagePlus className="h-4 w-4" />
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={onPick}
              className="hidden"
              disabled={files.length >= MAX_ATTACHMENTS}
            />
          </label>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Type a message…"
            maxLength={2000}
            className="h-10 flex-1 rounded-md border border-border bg-background px-3 text-sm focus:border-primary focus:outline-none"
          />
          <button
            type="submit"
            disabled={sending || (!draft.trim() && files.length === 0)}
            className="btn-primary px-4 text-sm disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </form>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Up to {MAX_ATTACHMENTS} images per message · JPG, PNG, WebP, GIF · max 10 MB each.
        </p>
      </div>
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            type="button"
            aria-label="Close"
            className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-black/70 text-white hover:bg-black"
            onClick={() => setLightboxUrl(null)}
          >
            <XIcon className="h-4 w-4" />
          </button>
          <img
            src={lightboxUrl}
            alt="Attachment full size"
            className="max-h-[90vh] max-w-full rounded-md"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </AppShell>
  );
}
